import { useState } from 'react';

export const useFilters = () => {
  // Transaction Review Filters
  const [reviewFilterStartDate, setReviewFilterStartDate] = useState('');
  const [reviewFilterEndDate, setReviewFilterEndDate] = useState('');
  const [reviewFilterDescription, setReviewFilterDescription] = useState('');
  const [reviewFilterAccount, setReviewFilterAccount] = useState('');
  const [reviewFilterCategory, setReviewFilterCategory] = useState('uncategorized');
  
  // Pagination for transaction review
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewRowsPerPage, setReviewRowsPerPage] = useState(25);
  
  // Rules category filter (independent from transaction filters)
  const [rulesCategoryFilter, setRulesCategoryFilter] = useState('');
  
  // All filters dialog state
  const [allFiltersDialogOpen, setAllFiltersDialogOpen] = useState(false);

  // Helper function to count active transaction filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (reviewFilterStartDate) count++;
    if (reviewFilterEndDate) count++;
    if (reviewFilterDescription) count++;
    if (reviewFilterAccount) count++;
    if (reviewFilterCategory && reviewFilterCategory !== 'uncategorized') count++;
    return count;
  };

  // Helper function to clear all transaction filters
  const clearAllFilters = () => {
    setReviewFilterStartDate('');
    setReviewFilterEndDate('');
    setReviewFilterCategory('uncategorized');
    setReviewFilterDescription('');
    setReviewFilterAccount('');
    setReviewPage(1);
  };

  // Filter transactions based on current filter state
  const filterTransactions = (transactions) => {
    return transactions.filter(t => {
      // Category filter
      if (reviewFilterCategory === 'uncategorized') {
        if (t.category && t.category !== 'uncategorized') return false;
      } else if (reviewFilterCategory && t.category !== reviewFilterCategory) {
        return false;
      }
      
      // Date filters
      if (reviewFilterStartDate && (!t.date || t.date < reviewFilterStartDate)) return false;
      if (reviewFilterEndDate && (!t.date || t.date > reviewFilterEndDate)) return false;
      
      // Text filters
      if (reviewFilterDescription && !(t.description || '').toLowerCase().includes(reviewFilterDescription.toLowerCase())) return false;
      if (reviewFilterAccount && !(t.account || '').toLowerCase().includes(reviewFilterAccount.toLowerCase())) return false;
      
      return true;
    });
  };

  // Get paginated transactions
  const getPaginatedTransactions = (filteredTransactions) => {
    const startIndex = (reviewPage - 1) * reviewRowsPerPage;
    const endIndex = startIndex + reviewRowsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  };

  // Filter rules based on category filter
  const filterRulesByCategory = (groupedRules) => {
    if (!rulesCategoryFilter) return groupedRules;
    return groupedRules.filter(group => group.category.id === rulesCategoryFilter);
  };

  // Handle pagination change
  const handlePageChange = (newPage) => {
    setReviewPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setReviewRowsPerPage(newRowsPerPage);
    setReviewPage(1); // Reset to first page
  };

  // Handle filter dialog actions
  const openAllFiltersDialog = () => {
    setAllFiltersDialogOpen(true);
  };

  const closeAllFiltersDialog = () => {
    setAllFiltersDialogOpen(false);
  };

  const clearAllFiltersAndCloseDialog = () => {
    clearAllFilters();
    setAllFiltersDialogOpen(false);
  };

  // Update individual filter functions (for controlled components)
  const updateReviewFilters = {
    setStartDate: (date) => {
      setReviewFilterStartDate(date);
      setReviewPage(1); // Reset pagination when filters change
    },
    setEndDate: (date) => {
      setReviewFilterEndDate(date);
      setReviewPage(1);
    },
    setDescription: (description) => {
      setReviewFilterDescription(description);
      setReviewPage(1);
    },
    setAccount: (account) => {
      setReviewFilterAccount(account);
      setReviewPage(1);
    },
    setCategory: (category) => {
      setReviewFilterCategory(category);
      setReviewPage(1);
    }
  };

  return {
    // Transaction Review Filter State
    reviewFilterStartDate,
    reviewFilterEndDate,
    reviewFilterDescription,
    reviewFilterAccount,
    reviewFilterCategory,
    
    // Pagination State
    reviewPage,
    reviewRowsPerPage,
    
    // Rules Filter State
    rulesCategoryFilter,
    
    // Dialog State
    allFiltersDialogOpen,
    
    // Filter Operations
    filterTransactions,
    getPaginatedTransactions,
    filterRulesByCategory,
    getActiveFilterCount,
    clearAllFilters,
    
    // Pagination Operations
    handlePageChange,
    handleRowsPerPageChange,
    
    // Dialog Operations
    openAllFiltersDialog,
    closeAllFiltersDialog,
    clearAllFiltersAndCloseDialog,
    
    // Filter Update Functions
    updateReviewFilters,
    setRulesCategoryFilter,
    
    // Direct setters (for components that need them)
    setReviewFilterStartDate,
    setReviewFilterEndDate,
    setReviewFilterDescription,
    setReviewFilterAccount,
    setReviewFilterCategory,
    setReviewPage,
    setReviewRowsPerPage,
    setAllFiltersDialogOpen
  };
};